// Foundations
import { Component, HostBinding, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// rxjs
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

// Services
import { CognitoService } from '@app/services/cognito.service';
import { AuthService } from '@app/auth/auth.service';
import { getLastAuthUser } from '@app/services/cognito.service';
import { GetUserFirstNameGQL } from '@app/services/graphql.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { AnalyticsService } from '@app/services/analytics.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
})
export class NavComponent implements OnInit {
  @HostBinding('class') class = 'lato';

  constructor(
    private router: Router,
    private cognitoService: CognitoService,
    private authService: AuthService,
    private getUserFirstNameQuery: GetUserFirstNameGQL,
    private loadingIndicatorService: LoadingIndicatorService,
    private _analytics: AnalyticsService,
    private matSnackBar: MatSnackBar
  ) {
    // Firstly fetch the "account ID"
    this.authService.fetchAccountId().subscribe({
      next: (accountId) => {
        // Handle the case that "account ID" could not be fetched
        if (!accountId) {
          // Invalid account
          this.matSnackBar.open(
            `We could not get the account info at this time. Please try re-login later.`,
            '',
            { duration: 2000 }
          );
          this.logout();
        }
      },
      error: (error) => {
        // handle error instead of finalize cause we only want to hide if there is an error
        this.loadingIndicatorService.hide();
      },
    });
  }

  userFirstName$: Observable<string>;
  isMenuOpen = false;
  @HostListener('window:scroll', ['$event'])
  onScroll(event) {
    const navMainStyle = document.getElementById('navMain').style;
    const logoStyle = document.getElementById('logo').style;
    const bookBtnStyle = document.getElementById('bookBtn').style;
    if (
      document.body.scrollTop > 80 ||
      document.documentElement.scrollTop > 80
    ) {
      navMainStyle.height = '3.75rem';
      navMainStyle.paddingLeft = '4.875rem';
      navMainStyle.boxShadow = '0 2px 4px 0 rgba(0, 0, 0, 0.15)';
      logoStyle.height = '2.5rem';
      bookBtnStyle.height = '2.5rem';
    } else {
      navMainStyle.height = '6.875rem';
      navMainStyle.paddingLeft = '3.125rem';
      navMainStyle.boxShadow = 'none';
      logoStyle.height = '4rem';
      bookBtnStyle.height = '3.125rem';
    }
  }

  @HostListener('window:mouseup', ['$event'])
  onMouseup(event) {
    const navMenu = document.getElementById('navMenu');
    if (event.target !== navMenu && !this.findParent(event.target, navMenu)) {
      this.isMenuOpen = false;
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyup(event) {
    if (event.key === 'Escape') {
      this.isMenuOpen = false;
    }
    if (this.isMenuOpen) {
      const navMenu = document.getElementById('navMenu');
      if (
        event.target !== navMenu &&
        !this.findParent(event.target, navMenu) &&
        !event.target.classList.contains('nav-button')
      ) {
        this.isMenuOpen = false;
      }
    }
  }

  ngOnInit(): void {
    this.userFirstName$ = this.authService.accountId$.pipe(
      filter((accountId) => !!accountId),
      switchMap((accountId) => {
        return this.getUserFirstNameQuery.fetch({ accountId }).pipe(
          map((res) => res.data.getAccount.firstName),
          take(1)
        );
      })
    );
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  getGreetingMsg() {
    const currentHour = new Date().getHours();
    const splitAfternoon = 12;
    const splitEvening = 17;

    if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
      return 'afternoon';
    } else if (currentHour >= splitEvening) {
      return 'evening';
    }
    return 'morning';
  }

  openMenu() {
    this._analytics.logLinkTap('landing_nav_menu');
    this.isMenuOpen = true;
  }

  logout() {
    this._analytics.logLinkTap('more_logout');

    this.loadingIndicatorService.show();
    setTimeout(() => {
      this.loadingIndicatorService.hide();
      this.cognitoService.signOut();
      this.authService.updateCurrentAccountId(null);
      this.router.navigate(['/welcome']);
    }, 1000);
  }

  logLandingPage(): void {
    this._analytics.logButtonTap('landingPage');
  }

  logCamperCams(): void {
    this._analytics.logButtonTap('landing_nav_camperCams');
  }

  logUpcoming(): void {
    this._analytics.logButtonTap('landing_nav_upcomingBooking');
  }

  logPackages(): void {
    this._analytics.logButtonTap('landing_nav_packages');
  }

  logReservation(): void {
    this._analytics.logLinkTap('landing_nav_book');
  }

  logMorePayments(): void {
    this._analytics.logLinkTap('more_payments');
  }

  logMoreMyProfile(): void {
    this._analytics.logLinkTap('more_myProfile');
  }

  logCamperList(): void {
    this._analytics.logLinkTap('more_camperList');
  }

  logComPref(): void {
    this._analytics.logLinkTap('more_communicationPreferences');
  }

  logChangePassword(): void {
    this._analytics.logLinkTap('more_changePassword');
  }
  logBWB(): void {
    this._analytics.logLinkTap('more_bowWowBuddies');
  }
  logLegal(): void {
    this._analytics.logLinkTap('more_legal');
  }
  logHelp(): void {
    this._analytics.logLinkTap('more_help');
  }
  // Private Methods
  // ------------------------------------------------------------------------------------

  private findParent(el, parent): boolean {
    while (el.parentNode) {
      el = el.parentNode;
      if (el === parent) {
        return true;
      }
    }
    return false;
  }
}
